import { createAction, handleActions } from 'redux-actions';
import {
  alphaSortObjectArray,
  groupArrayToDictByKeyValue,
} from 'utilities/arrayUtils';
import { isEqual } from 'lodash';

const initialState = {
  standardAnalytics: [],
  analyticLookup: {},
  equipmentBreakdown: undefined,
  isLoadingStandardAnalytics: true,
  isLoadingStandardAnalyticsDetails: {},
  analyticsDetailsLoadingCounter: 0,
  isDeployingAnalytics: {},
  selectedAnalytics: {},
};

const prefix = 'app/analyticsManager/';

export const types = {
  STANDARD_ANALYTICS_REQUEST: prefix + 'STANDARD_ANALYTICS_REQUEST',
  STANDARD_ANALYTICS_RESPONSE: prefix + 'STANDARD_ANALYTICS_RESPONSE',

  LOAD_ALL_ANALYTICS_DETAILS_REQUEST:
    prefix + 'LOAD_ALL_ANALYTICS_DETAILS_REQUEST',

  ANALYTIC_DETAILS_REQUEST: prefix + 'ANALYTIC_DETAILS_REQUEST',
  ANALYTIC_DETAILS_RESPONSE: prefix + 'ANALYTIC_DETAILS_RESPONSE',

  DEPLOY_ANALYTICS_REQUEST: prefix + 'DEPLOY_ANALYTICS_REQUEST',
  DEPLOY_ANALYTICS_RESPONSE: prefix + 'DEPLOY_ANALYTICS_RESPONSE',
  DEPLOY_ANALYTICS_ERROR: prefix + 'DEPLOY_ANALYTICS_ERROR',

  CLEAR_DEPLOY: prefix + 'CLEAR_DEPLOY',

  SELECT_BUILDINGS: prefix + 'SELECT_BUILDINGS',
  SELECT_ALL_ANALYTICS: prefix + 'SELECT_ALL_ANALYTICS',
  SELECT_ONLY_FULLY_MAPPED: prefix + 'SELECT_ONLY_FULLY_MAPPED',
  UPDATE_ANALYTIC_SELECTION: prefix + 'UPDATE_ANALYTIC_SELECTION',
};

export const actions = {
  standardAnalyticsRequest: createAction(types.STANDARD_ANALYTICS_REQUEST),
  standardAnalyticsResponse: createAction(types.STANDARD_ANALYTICS_RESPONSE),

  loadAllAnalyticDetailsRequest: createAction(
    types.LOAD_ALL_ANALYTICS_DETAILS_REQUEST
  ),

  analyticDetailsRequest: createAction(types.ANALYTIC_DETAILS_REQUEST),
  analyticDetailsResponse: createAction(types.ANALYTIC_DETAILS_RESPONSE),

  deployAnalyticsRequest: createAction(types.DEPLOY_ANALYTICS_REQUEST),
  deployAnalyticsResponse: createAction(types.DEPLOY_ANALYTICS_RESPONSE),
  deployAnalyticsError: createAction(types.DEPLOY_ANALYTICS_ERROR),

  clearDeploy: createAction(types.CLEAR_DEPLOY),

  selectBuildings: createAction(types.SELECT_BUILDINGS),

  selectAllAnalytics: createAction(types.SELECT_ALL_ANALYTICS),
  selectOnlyFullyMapped: createAction(types.SELECT_ONLY_FULLY_MAPPED),
  updateAnalyticSelection: createAction(types.UPDATE_ANALYTIC_SELECTION),
};

export const getStandardAnalytics = (siteId, buildingIds) => async (
  dispatch,
  _getState,
  { api }
) => {
  dispatch(actions.standardAnalyticsRequest());
  try {
    const { data } = await api.analyticsManager.getStandardAnalytics(
      siteId,
      buildingIds
    );
    const analytics = alphaSortObjectArray(data.analytics, 'analyticName');

    // Extract the Analytics and Equipment Classes into a dictionary for easy lookups.
    const analyticLookup = {};
    const equipmentClassLookup = {};
    const selectedAnalytics = {};

    for (let analytic of analytics) {
      const analyticId = analytic.analyticId;

      selectedAnalytics[analyticId] = {
        selected: true,
        excludedEquipment: new Set(),
        excludedEquipmentClasses: new Set(),
      };
      analyticLookup[analyticId] = analytic;

      for (let equipmentClass of analytic.equipmentClasses) {
        equipmentClassLookup[equipmentClass.equipmentClassId] =
          equipmentClass.equipmentClassName;
      }
    }

    const selectedBuildings = _getState().analyticsManager.selectedBuildings;

    // If the selected buildingIds have changed, do not accept these results.
    if (!isEqual(buildingIds, selectedBuildings)) {
      return;
    }

    return dispatch(
      actions.standardAnalyticsResponse({
        analytics,
        analyticLookup,
        equipmentClassLookup,
        selectedAnalytics,
      })
    );
  } catch (err) {
    return dispatch(actions.standardAnalyticsResponse(err));
  }
};

export const getStandardAnalyticsDetails = (
  siteId,
  buildingIds,
  standardAnalytics
) => async (dispatch, _getState, { api }) => {
  // Initialize a loading state where all analytics are pending.
  let loadingState = standardAnalytics.reduce((acc, curr) => {
    acc[curr.analyticId] = 'pending';
    return acc;
  }, {});

  // Loading counter set in store to track if the current while loop is stale or not.
  const loadingCounter =
    _getState().analyticsManager.analyticsDetailsLoadingCounter + 1;
  dispatch(
    actions.loadAllAnalyticDetailsRequest({ loadingState, loadingCounter })
  );

  do {
    // Only load point availability for 10 analytics at a given time.
    const concurrentLimit = 10;
    loadingState = _getState().analyticsManager
      .isLoadingStandardAnalyticsDetails;

    const loadingCount = Object.values(loadingState).filter(
      x => x === 'loading'
    ).length;

    let triggered = 0;

    for (let analytic of standardAnalytics) {
      // If we are in the process of loading the concurrentLimit breakout.
      if (loadingCount + triggered >= concurrentLimit) {
        break;
      }

      // Otherwise check if this analytic needs to be loaded.
      const notLoaded = loadingState[analytic.analyticId] === 'pending';

      if (notLoaded) {
        getStandardAnalyticDetails(
          dispatch,
          api,
          _getState,
          siteId,
          buildingIds,
          analytic
        );

        triggered += 1;
      }
    }

    // Wait one second before attempting to load more.
    await new Promise(x => setTimeout(x, 1000));

    // Break out of loop if the current loading counter is stale or all details are loaded.
  } while (
    !isLoadingCounterStale(loadingCounter, _getState) &&
    !areAnalyticsDetailsLoaded(_getState)
  );
};

/* Helper to determine if the analytics details are fully loaded. 
 */
const areAnalyticsDetailsLoaded = _getState => {
  const loadingState = _getState().analyticsManager
    .isLoadingStandardAnalyticsDetails;

  return Object.values(loadingState).every(x =>
    ['loaded', 'failed'].includes(x)
  );
};

/* Helper function to determine if the counter for our analytic 
 */
const isLoadingCounterStale = (counter, _getState) => {
  const currentCounter = _getState().analyticsManager
    .analyticsDetailsLoadingCounter;
  return counter !== currentCounter;
};

const getStandardAnalyticDetails = async (
  dispatch,
  api,
  _getState,
  siteId,
  buildingIds,
  analytic
) => {
  // Indicate that we are now loading this analytic.
  dispatch(
    actions.analyticDetailsRequest({
      analyticId: analytic.analyticId,
    })
  );

  try {
    const {
      analyticPointAvailability,
      equipmentClassPointAvailability,
      equipmentBreakdown,
    } = await getAnalyticPointAvailability(
      api,
      _getState,
      siteId,
      buildingIds,
      analytic.analyticId,
      analytic.equipmentClasses.map(x => x.equipmentClassId)
    );

    const deployedTasks = await getDeployedTasks(
      api,
      _getState,
      siteId,
      buildingIds,
      analytic.analyticId
    );

    const defaultUnselected = {
      [analytic.analyticId]: {
        selected: false,
        excludedEquipment: new Set(equipmentBreakdown.map(x => x.equipmentId)),
        excludedEquipmentClasses: new Set(
          analytic.equipmentClasses.map(x => x.equipmentClassId)
        ),
      },
    };

    // Check the state to determine if we are still operating on the same building
    // selection. If not, discard this data as it is now inaccurate.
    const selectedBuildingIds = _getState().analyticsManager.selectedBuildings;
    if (!isEqual(buildingIds, selectedBuildingIds)) {
      return;
    }

    dispatch(
      actions.analyticDetailsResponse({
        analyticId: analytic.analyticId,
        analyticPointAvailability,
        equipmentClassPointAvailability,
        equipmentBreakdown,
        deployedTasks,
        defaultUnselected,
      })
    );
  } catch (e) {
    // If the building selection has change it is reasonable for some of this code to have failed
    // after an API response. Only raise the error if the building selection is the same.
    const selectedBuildingIds = _getState().analyticsManager.selectedBuildings;
    if (isEqual(buildingIds, selectedBuildingIds)) {
      console.error(
        `Error while loading analytic: ${analytic.analyticId}: ${e}.`
      );
      throw e;
    }
  }
};

/* Get's the point availability for a specific analytic. Rolls up the point
 * availability for the whole analytic as well as the specific equipment classes.
 */
const getAnalyticPointAvailability = async (
  api,
  _getState,
  siteId,
  buildingIds,
  analyticId,
  equipmentClassIds
) => {
  const { data } = await api.analyticsManager.getAnalyticPointAvailability(
    siteId,
    buildingIds,
    analyticId,
    equipmentClassIds
  );

  const state = _getState().analyticsManager;
  const analyticLookup = state.analyticLookup;
  const equipmentClassLookup = state.equipmentClassLookup;

  // Used to populate child equipment class tables.
  // Initialize all to empty in case there are no equipment for that equipmentClass.
  const equipmentClassPointAvailability = Object.fromEntries(
    equipmentClassIds.map(x => [
      x,
      {
        total: 0,
        fullyMapped: 0,
        notMapped: 0,
        partiallyMapped: 0,
      },
    ])
  );

  // Used to populate the task list table.
  const equipmentBreakdown = [];

  for (let equipmentClass of data.equipmentClasses) {
    // Create an object for this equipment class to track the following values.
    // total - How many pieces of equipment are there
    // fullyMapped - How many pieces of equipment have all required points
    // partiallyMapped - How many pieces of equipment have this variable.
    // notMapped - How many pieces of equipment have none of the required points.
    const tracker = {
      total: equipmentClass.equipmentPointAvailibity.length,
      fullyMapped: 0,
      notMapped: 0,
      partiallyMapped: 0,
    };

    for (let equipment of equipmentClass.equipmentPointAvailibity) {
      // Add this to our equipment breakdown.
      const equipmentSummary = {
        analyticId,
        analyticName: analyticLookup[analyticId].analyticName,
        equipmentName: equipment.equipmentName,
        equipmentId: equipment.equipmentId,
        equipmentClass: equipmentClass.equipmentClassId,
        equipmentClassName:
          equipmentClassLookup[equipmentClass.equipmentClassId],
        buildingName: equipment.buildingName,
        mappedPoints: equipment.pointAvailability
          .filter(x => x.points.length)
          .map(x => x.variable),
        missingPoints: equipment.pointAvailability
          .filter(x => x.points.length === 0)
          .map(x => x.variable),
      };

      equipmentBreakdown.push(equipmentSummary);

      // Increment the tracker based on the state of this equipments mapping.
      if (equipmentSummary.missingPoints.length === 0) {
        tracker.fullyMapped += 1;
      } else if (equipmentSummary.mappedPoints.length === 0) {
        tracker.notMapped += 1;
      } else {
        tracker.partiallyMapped += 1;
      }
    }

    // Assign the value to the dictionary.
    equipmentClassPointAvailability[equipmentClass.equipmentClassId] = tracker;
  }

  const analyticPointAvailability = Object.values(
    equipmentClassPointAvailability
  ).reduce(
    (accumulator, current) => {
      // Iterate over all of the keys (variables).
      for (let key of Object.keys(current)) {
        // Either assign the value if it doesn't exist or increment it.
        if (!(key in accumulator)) {
          accumulator[key] = current[key];
        } else {
          accumulator[key] += current[key];
        }
      }
      return accumulator;
    },
    {
      total: 0,
      fullyMapped: 0,
      notMapped: 0,
      partiallyMapped: 0,
    }
  );

  return {
    analyticPointAvailability,
    equipmentClassPointAvailability,
    equipmentBreakdown,
  };
};

/* Get's the deployed tasks for each analytic.
 */
const getDeployedTasks = async (
  api,
  _getState,
  siteId,
  buildingIds,
  analyticId
) => {
  const { data } = await api.analyticsManager.getDeployedTasks(
    siteId,
    buildingIds,
    analyticId
  );

  return data.deployedTasks;
};

export const selectBuildings = buildingIds => async (
  dispatch,
  _getState,
  { api }
) => {
  return dispatch(actions.selectBuildings(buildingIds));
};

export const selectAnalytic = (analyticId, selected) => async (
  dispatch,
  _getState,
  { api }
) => {
  const analyticDetails = _getState().analyticsManager.analyticLookup[analyticId];
  const equipmentBreakdown = _getState().analyticsManager.equipmentBreakdown;
  const updatedAnalyticSelection = {
    [analyticId]: {
      excludedEquipment: selected
        ? new Set()
        : new Set(equipmentBreakdown.filter(x => x.analyticId === analyticId).map(x => x.equipmentId)),
      excludedEquipmentClasses: selected
        ? new Set()
        : new Set(analyticDetails.equipmentClasses.map(x => x.equipmentClassId)),
      selected,
    },
  };

  return dispatch(
    actions.updateAnalyticSelection({
      updatedAnalyticSelection,
    })
  );
};

export const selectAllAnalytics = (selected) => async (
  dispatch,
  _getState,
  { api }
) => {
  const selectedAnalytics = _getState().analyticsManager.selectedAnalytics;
  const equipmentBreakdown = _getState().analyticsManager.equipmentBreakdown;

  const newSelected = {};
  for (let item of Object.keys(selectedAnalytics)) {
    const analyticId = parseInt(item);
    const analyticDetails = _getState().analyticsManager.analyticLookup[analyticId];
    newSelected[analyticId] = {
      excludedEquipment: selected
        ? new Set()
        : new Set(equipmentBreakdown.filter(x => x.analyticId === analyticId).map(x => x.equipmentId)),
      excludedEquipmentClasses: selected
        ? new Set()
        : new Set(analyticDetails.equipmentClasses.map(x => x.equipmentClassId)),
      selected,
    };
  }

  return dispatch(
    actions.selectAllAnalytics({
      selectedAnalytics: newSelected,
    })
  );
};

export const selectEquipment = (
  analyticId,
  equipmentClassId,
  equipmentId,
  selected
) => async (dispatch, _getState, { api }) => {
  const selectedAnalytics = _getState().analyticsManager.selectedAnalytics;
  const equipmentBreakdown = _getState().analyticsManager.equipmentBreakdown;
  const analyticDetails = _getState().analyticsManager.analyticLookup[analyticId];
  const analytic = selectedAnalytics[analyticId];

  // If it is selected it is not excluded, remove.
  if (selected) {
    // If the analytic was not selected, select the analytic, but exclude all other equipment.
    if (!analytic.selected) {
      analytic.selected = selected;
      const equipmentIdsForAnalytic = equipmentBreakdown
        .filter(x => x.analyticId === analyticId)
        .map(x => x.equipmentId);
      analytic.excludedEquipment = analytic.excludedEquipment.union(
        new Set(equipmentIdsForAnalytic)
      );
    }

    // If the equipment class was not selected, select it, and exclude all child equipments of it.
    if (analytic.excludedEquipmentClasses.has(equipmentClassId)) {
      const equipmentIdsForEquipmentClass = equipmentBreakdown
        .filter(
          x =>
            x.analyticId === analyticId && x.equipmentClass === equipmentClassId
        )
        .map(x => x.equipmentId);
      analytic.excludedEquipment = analytic.excludedEquipment.union(
        new Set(equipmentIdsForEquipmentClass)
      );
    }

    analytic.excludedEquipmentClasses.delete(equipmentClassId);
    analytic.excludedEquipment.delete(equipmentId);
  }
  // Otherwise add to excluded.
  else {
    analytic.excludedEquipment.add(equipmentId);

    // Check to determine if all equipment from this equipmentClass is excluded, then exclude equipClass.
    const equipmentIdsForEquipmentClass = equipmentBreakdown
      .filter(x => x.analyticId === analyticId && x.equipmentClass === equipmentClassId)
      .map(x => x.equipmentId);

    let equipClassFullyExcluded = true;
    for (const item of equipmentIdsForEquipmentClass) {
      if (!analytic.excludedEquipment.has(item)) {
        equipClassFullyExcluded = false;
        break;
      }
    }

    if (equipClassFullyExcluded) {
      analytic.excludedEquipmentClasses.add(equipmentClassId);
    }

    // Check to determine if all equipmentClasses are excluded, then exclude analytic.
    const analyticEquipmentClassIds = analyticDetails.equipmentClasses.map(x => x.equipmentClassId);
    let analyticFullyExcluded = true;
    for (const item of analyticEquipmentClassIds) {
      if (!analytic.excludedEquipmentClasses.has(item)) {
        analyticFullyExcluded = false;
        break;
      }
    }

    if (analyticFullyExcluded) {
      analytic.selected = false;
    }
  }

  const updatedAnalyticSelection = {
    [analyticId]: { ...analytic },
  };

  return dispatch(
    actions.updateAnalyticSelection({
      updatedAnalyticSelection,
    })
  );
};

export const selectEquipmentClass = (
  analyticId,
  equipmentClassId,
  childEquipmentIds,
  selected
) => async (dispatch, _getState, { api }) => {
  const analytic = _getState().analyticsManager.selectedAnalytics[analyticId];
  const analyticDetails = _getState().analyticsManager.analyticLookup[
    analyticId
  ];

  // If it is selected it is not excluded, remove.
  if (selected) {
    if (!analytic.selected) {
      analytic.selected = selected;
      analytic.excludedEquipmentClasses = new Set(
        analyticDetails.equipmentClasses.map(x => x.equipmentClassId)
      );
    }

    analytic.excludedEquipmentClasses.delete(equipmentClassId);
    // Only remove equipment from this equipment class from the exclusion list.
    analytic.excludedEquipment = analytic.excludedEquipment.difference(
      new Set(childEquipmentIds)
    );
  }
  // Otherwise add to excluded.
  else {
    analytic.excludedEquipmentClasses.add(equipmentClassId);
    analytic.excludedEquipment = analytic.excludedEquipment.union(
      new Set(childEquipmentIds)
    );

    // Check to determine if all equipmentClasses are excluded, then exclude analytic.
    const analyticEquipmentClassIds = analyticDetails.equipmentClasses.map(x => x.equipmentClassId);
    let analyticFullyExcluded = true;
    for (const item of analyticEquipmentClassIds) {
      if (!analytic.excludedEquipmentClasses.has(item)) {
        analyticFullyExcluded = false;
        break;
      }
    }

    if (analyticFullyExcluded) {
      analytic.selected = false;
    }
  }

  const updatedAnalyticSelection = {
    [analyticId]: { ...analytic },
  };

  return dispatch(
    actions.updateAnalyticSelection({
      updatedAnalyticSelection,
    })
  );
};

/* Defaults everything back to excluded then selects only the 
 * equipment, and associated equip classes/ analytics, that are fully mapped.
 */
export const selectOnlyFullyMapped = () => async (
  dispatch,
  _getState,
  { api }
) => {
  const standardAnalytics = _getState().analyticsManager.standardAnalytics;
  const selectedAnalytics = _getState().analyticsManager.selectedAnalytics;

  // Loop over all analytics.
  for (let analytic of standardAnalytics) {
    const equipmentClassBreakdowns = analytic.analyticEquipmentClassBreakdown;
    const newSelection = { 
      selected: false, 
      excludedEquipment: new Set(), 
      excludedEquipmentClasses: new Set(),
    };

    // Loop over all of the equip classes.
    for (let key of Object.keys(equipmentClassBreakdowns)) {
      const equipmentClassId = Number(key);
      const breakdown = equipmentClassBreakdowns[equipmentClassId];
      
      // Default to excluded.
      newSelection.excludedEquipmentClasses.add(equipmentClassId);

      // Loop over all equipment within the equip class.
      for (let equipment of breakdown) {
        // Default to excluded.
        newSelection.excludedEquipment.add(equipment.equipmentId);

        const isDeployed = analytic.deployedTasks.includes(equipment.equipmentId);
        if (equipment.missingPoints.length === 0 && !isDeployed) {
          // The equipment is fully mapped and not deployed.
          // Remove both the equipment and parent equipment class from exclusion.
          newSelection.excludedEquipment.delete(equipment.equipmentId);
          newSelection.excludedEquipmentClasses.delete(equipmentClassId);

          // Since we have selected at least one child we need to select the analytic as well.
          newSelection.selected = true;
        }
      }
    }

    selectedAnalytics[analytic.analyticId] = newSelection;
  }

  dispatch(actions.selectOnlyFullyMapped({selectedAnalytics}));
};

export const deployAnalytic = (
  analytic,
  siteId,
  buildingIds,
  excludedEquipmentClassIds,
  deselectedEquipmentIds
) => async (dispatch, _getState, { api }) => {
  try {
    dispatch(
      actions.deployAnalyticsRequest({ analyticId: analytic.analyticId })
    );

    // Construct the INCLUDED equipmentClassId set, by removing the excluded equipment classes.
    const equipmentClassIds = new Set(
      analytic.equipmentClasses.map(x => x.equipmentClassId)
    );
    for (let equipmentClassId of excludedEquipmentClassIds) {
      equipmentClassIds.delete(equipmentClassId);
    }

    // Construct the EXCLUDED equipmentId set, by adding deselected equipment ID as well as already deployed ones.
    const excludedEquipmentIds = new Set([
      ...deselectedEquipmentIds,
      ..._getState()
        .analyticsManager.standardAnalytics.filter(x => x.analyticId == analytic.analyticId)
        .flatMap(x => x.deployedTasks)
        .map(x => x.equipmentId),
    ]);

    await api.analyticsManager.deployAnalytic(
      analytic.analyticId,
      analytic.analyticName,
      siteId,
      buildingIds,
      Array.from(equipmentClassIds),
      Array.from(excludedEquipmentIds)
    );

    return dispatch(
      actions.deployAnalyticsResponse({ analyticId: analytic.analyticId })
    );
  } catch (e) {
    return dispatch(
      actions.deployAnalyticsError({ analyticId: analytic.analyticId })
    );
  }
};

export const clearDeploy = () => async (dispatch, _getState, { api }) => {
  return dispatch(actions.clearDeploy());
};

export default handleActions(
  {
    [actions.standardAnalyticsRequest]: {
      next: state => ({
        ...state,
        isLoadingStandardAnalytics: true,
      }),
    },
    [actions.standardAnalyticsResponse]: {
      next: (state, { payload }) => {
        return {
          ...state,
          standardAnalytics: payload.analytics,
          equipmentClassLookup: payload.equipmentClassLookup,
          analyticLookup: payload.analyticLookup,
          isLoadingStandardAnalytics: false,
          selectedAnalytics: payload.selectedAnalytics,
        };
      },
      throw: (state, { payload }) => ({
        ...state,
        standardAnalyticsError: payload.message,
        isLoadingStandardAnalytics: false,
      }),
    },
    [actions.loadAllAnalyticDetailsRequest]: {
      next: (state, { payload }) => ({
        ...state,
        isLoadingStandardAnalyticsDetails: payload.loadingState,
        analyticsDetailsLoadingCounter: payload.loadingCounter,
      }),
    },
    [actions.analyticDetailsRequest]: {
      next: (state, { payload }) => ({
        ...state,
        isLoadingStandardAnalyticsDetails: {
          ...state.isLoadingStandardAnalyticsDetails,
          [payload.analyticId]: 'loading',
        },
      }),
    },
    [actions.analyticDetailsResponse]: {
      next: (state, { payload }) => {
        const {
          analyticId,
          analyticPointAvailability,
          equipmentClassPointAvailability,
          equipmentBreakdown,
          deployedTasks,
          defaultUnselected,
        } = payload;
        let updatedBreakdown =
          state.equipmentBreakdown != null
            ? state.equipmentBreakdown.concat(equipmentBreakdown)
            : equipmentBreakdown;

        updatedBreakdown = alphaSortObjectArray(
          updatedBreakdown,
          'analyticName'
        );

        const equipClassGroupedBreakdown = groupArrayToDictByKeyValue(
          equipmentBreakdown,
          'equipmentClass'
        );

        return {
          ...state,
          // Indicate that this analyticId has been loaded.
          isLoadingStandardAnalyticsDetails: {
            ...state.isLoadingStandardAnalyticsDetails,
            [analyticId]: 'loaded',
          },
          // Add all of the results to the correct standard analytic.
          standardAnalytics: state.standardAnalytics.map(x => {
            return x.analyticId !== analyticId
              ? x
              : {
                  ...x,
                  deployedTasks: deployedTasks,
                  analyticPointAvailability: analyticPointAvailability,
                  equipmentClassPointAvailability: equipmentClassPointAvailability,
                  analyticEquipmentClassBreakdown: equipClassGroupedBreakdown,
                };
          }),
          // Either initialize or concat.
          equipmentBreakdown: updatedBreakdown,
          selectedAnalytics: {
            ...state.selectedAnalytics,
            ...defaultUnselected,
          },
        };
      },
    },
    [actions.deployAnalyticsRequest]: {
      next: (state, { payload }) => {
        return {
          ...state,
          isDeployingAnalytics: {
            ...state.isDeployingAnalytics,
            [payload.analyticId]: 'deploying',
          },
        };
      },
    },
    [actions.deployAnalyticsResponse]: {
      next: (state, { payload }) => {
        return {
          ...state,
          isDeployingAnalytics: {
            ...state.isDeployingAnalytics,
            [payload.analyticId]: 'deployed',
          },
        };
      },
    },
    [actions.deployAnalyticsError]: {
      next: (state, { payload }) => {
        return {
          ...state,
          isDeployingAnalytics: {
            ...state.isDeployingAnalytics,
            [payload.analyticId]: 'failed',
          },
        };
      },
    },
    [actions.clearDeploy]: {
      next: state => {
        return {
          ...state,
          isDeployingAnalytics: {},
        };
      },
    },
    [actions.selectBuildings]: {
      next: (state, { payload }) => {
        return {
          ...state,
          selectedBuildings: [...payload],
          // Reset the analytics detail tracker to indicate we can reload the details now.
          isLoadingStandardAnalyticsDetails: {},
          equipmentBreakdown: undefined,
        };
      },
    },
    [actions.selectAllAnalytics]: {
      next: (state, { payload }) => {
        return {
          ...state,
          // Spread to a new object to force a re-render on selection.
          selectedAnalytics: { ...payload.selectedAnalytics },
        };
      },
    },
    [actions.selectOnlyFullyMapped]: {
      next: (state, { payload }) => {
        return {
          ...state,
          // Spread to a new object to force a re-render on selection.
          selectedAnalytics: { ...payload.selectedAnalytics },
        };
      },
    },
    [actions.updateAnalyticSelection]: {
      next: (state, { payload }) => {
        return {
          ...state,
          // Spread to a new object to force a re-render on selection.
          selectedAnalytics: {
            ...state.selectedAnalytics,
            ...payload.updatedAnalyticSelection,
          },
        };
      },
    },
  },
  initialState
);
